import type {
	AssetKey,
	FuturesMarketAsset,
	PerpsProvider,
	PositionSide,
	SynthAssetKeysV3,
	TokenTickersAndEth,
} from '@kwenta/sdk/types'
import { formatDollars } from '@kwenta/sdk/utils'
import type Wei from '@kwenta/wei'
import { type ReactElement, memo } from 'react'
import styled from 'styled-components'

import Badge from 'components/Badge'
import ColoredPrice from 'components/ColoredPrice'
import Currency from 'components/Currency'
import type { CurrencyIconProps } from 'components/Currency/CurrencyIcon/CurrencyIcon'
import { PROVIDER_CHAIN_ICONS } from 'constants/network'
import type { PricesInfo } from 'state/prices/types'

import PositionType from '../PositionType'

type Props = {
	perpsProvider: PerpsProvider
	assetKey: AssetKey | SynthAssetKeysV3 | TokenTickersAndEth
	marketName: string
	side?: PositionSide
	infoLabel?: string
	badge?: ReactElement
	price?: Wei
	priceInfo?: PricesInfo<Wei>
	iconProps?: Omit<CurrencyIconProps, 'currencyKey'>
}

const TableMarketDetails = memo(
	({
		marketName,
		assetKey,
		side,
		infoLabel,
		badge,
		price,
		priceInfo,
		iconProps,
		...props
	}: Props) => {
		return (
			<MarketContainer {...props}>
				<IconContainer>
					<StyledCurrencyIcon currencyKey={assetKey} width={28} height={28} {...iconProps} />
					<ProviderIconContainer>{PROVIDER_CHAIN_ICONS[props.perpsProvider]}</ProviderIconContainer>
				</IconContainer>

				<div>
					<StyledText>
						{marketName}
						{badge}
					</StyledText>
					{infoLabel && <StyledValue>{infoLabel}</StyledValue>}
					{side && <PositionType side={side} variant={'text'} />}
					{price && priceInfo && (
						<ColoredPrice priceChange={priceInfo.change}>
							{formatDollars(price, {
								suggestDecimals: true,
								suggestDecimalsForAsset: assetKey as FuturesMarketAsset,
							})}
						</ColoredPrice>
					)}
				</div>
			</MarketContainer>
		)
	}
)

export default TableMarketDetails

export const WarningBadge = styled(Badge)`
	background: ${(props) => props.theme.colors.selectedTheme.red};
	padding: 1px 5px;
	line-height: 9px;
	margin-left: 6px;
`

const StyledCurrencyIcon = styled(Currency.Icon)`
	width: 28px;
	height: 28px;
`

const StyledText = styled.div`
	display: flex;
	align-items: center;
	grid-column: 2;
	grid-row: 1;
`

const MarketContainer = styled.div`
	display: flex;
	align-items: center;
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
`

const StyledValue = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	font-weight: 400;
	font-size: 12px;
	grid-column: 2;
	grid-row: 2;
`

const IconContainer = styled.div`
	position: relative;
	margin-right: 8px;
	margin-left: -4px;
`
const ProviderIconContainer = styled.div`
	position: absolute;
	bottom: -5px;
	right: -3px;
`
